import { yupResolver } from '@hookform/resolvers/yup';
import { BlogFont, BlogHomeCustomization, IOperationResultNoValue } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useImageAttachmentMutation } from '../../../../../queries/Attachments/useImageAttachmentMutation';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { Button } from '../../../../../web/components-v2/Button';
import { Field, FieldError, FieldLabel } from '../../../../../web/components-v2/Field';
import { Input } from '../../../../../web/components-v2/Input';
import { ImageFileChooserModal } from '../../../../../web/components/ImageFileChooserModal';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { Toggle } from '../../../../../web/components/Toggle';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { Row } from '../../../../components/Layout';
import { ColorField } from '../components/ColorField';
import { SettingCard } from '../components/SettingCard';
import {
	DEFAULT_BLOG_LINK_FONT_SIZE_PX,
	DEFAULT_FONT_VALUES,
	DEFAULT_HOME_FORM_VALUES,
	MAX_FONT_SIZE_PX,
	MIN_FONT_SIZE_PX,
} from './constants';
import { getFontSizePlaceholder, parseOptionalFontSize } from './fontSizeHelpers';
import { ResponsiveFontSizeInputs } from './ResponsiveFontSizeInputs';
import { blogHomeSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface HomeDesignFormProps {
	customContent?: BlogHomeCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
	onSavingChange?: (saving: boolean) => void;
}

export interface HomeDesignFormRef {
	submit?: () => void;
}

const mergeHomeFormValues = (customContent?: BlogHomeCustomization): BlogHomeCustomization => ({
	...DEFAULT_HOME_FORM_VALUES,
	...customContent,
	font: {
		...DEFAULT_HOME_FORM_VALUES.font,
		...(customContent?.font ?? {}),
	},
	blogTitle: {
		...DEFAULT_HOME_FORM_VALUES.blogTitle,
		...(customContent?.blogTitle ?? {}),
	},
	cardStyle: {
		...DEFAULT_HOME_FORM_VALUES.cardStyle,
		...(customContent?.cardStyle ?? {}),
		titleStyle: {
			...DEFAULT_HOME_FORM_VALUES.cardStyle.titleStyle,
			...(customContent?.cardStyle?.titleStyle ?? {}),
		},
		linkStyle: {
			...DEFAULT_HOME_FORM_VALUES.cardStyle.linkStyle,
			...(customContent?.cardStyle?.linkStyle ?? {}),
		},
	},
});

export const HomeDesignForm = forwardRef<HomeDesignFormRef, HomeDesignFormProps>(
	({ customContent, onFormDirty, onUpdateSuccess, onSavingChange }, ref) => {
		const errorMessages = useErrorMessages();
		const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
		const { accountId, impersonationContext } = useAccountContext();

		const {
			handleSubmit,
			register,
			reset,
			setValue,
			getValues,
			watch,
			formState: { errors, isDirty },
		} = useForm<BlogHomeCustomization>({
			defaultValues: DEFAULT_HOME_FORM_VALUES,
			mode: 'onBlur',
			resolver: yupResolver(blogHomeSchema),
		});
		const metadataToggle = watch('cardStyle.showAuthorMetaData');
		const nookToggle = watch('hideNook');
		const font = watch('font', DEFAULT_FONT_VALUES);

		// Watch color values for ColorField components
		const pageBackgroundColor = watch('pageBackgroundColor');
		const blogTitleColor = watch('blogTitle.color');
		const blogTitleFontSize = watch('blogTitle.fontSizePx');
		const cardTitleColor = watch('cardStyle.titleStyle.color');
		const cardTitleFontSize = watch('cardStyle.titleStyle.fontSizePx');
		const cardLinkColor = watch('cardStyle.linkStyle.color');
		const cardLinkFontSize = watch('cardStyle.linkStyle.fontSizePx');

		const handleToggleMetadata = (value: boolean) => {
			setValue('cardStyle.showAuthorMetaData', value, { shouldDirty: true });
		};
		const handleNookToggle = (value: boolean) => {
			setValue('hideNook', value, { shouldDirty: true });
		};
		const handleSelectFont = (selectedFont: BlogFont) => {
			setValue('font', selectedFont, { shouldDirty: true });
		};

		const updateHomeMutation = useFindOrCreateAndUpdateBlogStyleMutation({
			onSuccess: () => {
				reset(getValues());
				onUpdateSuccess();
				toast.success('Saved changes successfully');
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
			},
		});

		const onValid: SubmitHandler<BlogHomeCustomization> = (data, event) => {
			event?.preventDefault?.();
			event?.stopPropagation?.();

			updateHomeMutation.mutate({
				id: accountId,
				impersonationContext,
				style: data,
			});
		};

		const onInvalid = (validationErrors: FieldErrors<BlogHomeCustomization>) => {
			const validationErrorMessages = Object.entries(validationErrors)
				.map(([field, error]) => `${field}: ${error?.message || 'invalid'}`)
				.join(', ');
			toast.error(`Validation failed: ${validationErrorMessages}`);
		};

		const imageUpload = useImageAttachmentMutation({
			onError: error => errorMessages.pushApiError(error, 'Failed to upload image'),
			onSuccess: result => {
				setValue('headerImage', result.at(0), { shouldDirty: true });
			},
		});

		// Sync customContent to form values when it changes while preserving nested defaults.
		useEffect(() => {
			if (!customContent) {
				return;
			}

			const mergedCustomContent = mergeHomeFormValues(customContent);
			if (!equal(mergedCustomContent, getValues())) {
				reset(mergedCustomContent);
			}
		}, [customContent, getValues, reset]);

		useEffect(() => {
			if (isDirty) {
				onFormDirty();
			}
		}, [isDirty, onFormDirty]);

		useEffect(() => {
			onSavingChange?.(updateHomeMutation.isPending);
		}, [updateHomeMutation.isPending, onSavingChange]);

		useImperativeHandle(ref, () => ({
			submit: () => handleSubmit(onValid, onInvalid)(),
		}));

		return (
			<>
				<TemplateFormWrapper
					chromeless
					title='Customize Blog Home Page'
					isLoading={updateHomeMutation.isPending}
					errors={errors}
					onSubmit={handleSubmit(onValid, onInvalid)}
				>
					<SettingCard title='Background'>
						<ColorField
							id='home-page-bg'
							label='Page Background Color'
							value={pageBackgroundColor}
							fallback='#FFFFFF'
							onChange={color => setValue('pageBackgroundColor', color, { shouldDirty: true })}
						/>
						<Button variant='secondary' id='upload-header-button' type='button' onClick={() => setImageModalOpen(true)}>
							Upload Header Image
						</Button>
						{watch('headerImage')?.id && (
							<Button
								variant='destructive-secondary'
								id='remove-header-button'
								type='button'
								onClick={() =>
									setValue('headerImage', { fileName: '', fileSize: 0, id: '', url: '' }, { shouldDirty: true })
								}
							>
								<TrashIcon /> Remove Header Image
							</Button>
						)}
					</SettingCard>

					<SettingCard title='Font'>
						<FontSelect
							initialValue={customContent?.font}
							onChange={handleSelectFont}
							errors={errors?.font}
							value={font}
						/>
					</SettingCard>

					<SettingCard title='Section Title'>
						<Field>
							<FieldLabel htmlFor='home-blog-title'>Title Text</FieldLabel>
							<Input id='home-blog-title' {...register('blogTitle.title')} />
							<FieldError errors={errors?.blogTitle?.title ? [errors.blogTitle.title] : []} />
						</Field>
						<ResponsiveFontSizeInputs
							desktopError={errors?.blogTitle?.fontSizePx}
							desktopInputProps={{
								placeholder: getFontSizePlaceholder(blogTitleFontSize),
								...register('blogTitle.fontSizePx', { setValueAs: parseOptionalFontSize }),
							}}
							desktopLabel='Title Size'
							max={MAX_FONT_SIZE_PX}
							min={MIN_FONT_SIZE_PX}
							mobileError={errors?.blogTitle?.mobileFontSizePx}
							mobileInputProps={{
								placeholder: getFontSizePlaceholder(undefined, blogTitleFontSize),
								...register('blogTitle.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
							}}
							mobileLabel='Mobile Title Size'
							step={1}
						/>
						<ColorField
							id='home-blog-title-color'
							label='Color'
							value={blogTitleColor}
							fallback='#000000'
							onChange={color => setValue('blogTitle.color', color, { shouldDirty: true })}
						/>
						<Row className={css(bs.itemsCenter, bs.gap2)}>
							<Toggle id='nook-toggle' isOn={nookToggle} onToggleCheckChanged={handleNookToggle} text='Hide Nook' />
							<InfoPopover message='This setting will be used for the blog home page, as well as individual posts' />
						</Row>
					</SettingCard>

					<SettingCard title='Individual Post'>
						<ResponsiveFontSizeInputs
							desktopError={errors?.cardStyle?.titleStyle?.fontSizePx}
							desktopInputProps={{
								placeholder: getFontSizePlaceholder(cardTitleFontSize),
								...register('cardStyle.titleStyle.fontSizePx', { setValueAs: parseOptionalFontSize }),
							}}
							desktopLabel='Post Title Size'
							max={MAX_FONT_SIZE_PX}
							min={MIN_FONT_SIZE_PX}
							mobileError={errors?.cardStyle?.titleStyle?.mobileFontSizePx}
							mobileInputProps={{
								placeholder: getFontSizePlaceholder(undefined, cardTitleFontSize),
								...register('cardStyle.titleStyle.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
							}}
							mobileLabel='Mobile Post Title Size'
							step={1}
						/>
						<ColorField
							id='home-card-title-color'
							label='Title Color'
							value={cardTitleColor}
							fallback='#000000'
							onChange={color => setValue('cardStyle.titleStyle.color', color, { shouldDirty: true })}
						/>
						<ResponsiveFontSizeInputs
							desktopError={errors?.cardStyle?.linkStyle?.fontSizePx}
							desktopInputProps={{
								placeholder: getFontSizePlaceholder(cardLinkFontSize, DEFAULT_BLOG_LINK_FONT_SIZE_PX),
								...register('cardStyle.linkStyle.fontSizePx', { setValueAs: parseOptionalFontSize }),
							}}
							desktopLabel='Read More Link Size'
							max={MAX_FONT_SIZE_PX}
							min={MIN_FONT_SIZE_PX}
							mobileError={errors?.cardStyle?.linkStyle?.mobileFontSizePx}
							mobileInputProps={{
								placeholder: getFontSizePlaceholder(undefined, cardLinkFontSize ?? DEFAULT_BLOG_LINK_FONT_SIZE_PX),
								...register('cardStyle.linkStyle.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
							}}
							mobileLabel='Mobile Read More Link Size'
							step={1}
						/>
						<ColorField
							id='home-card-link-color'
							label='Link Color'
							value={cardLinkColor}
							fallback='#599EED'
							onChange={color => setValue('cardStyle.linkStyle.color', color, { shouldDirty: true })}
						/>
						<Row className={css(bs.itemsCenter, bs.gap2)}>
							<Toggle
								id='metadata-toggle'
								isOn={metadataToggle}
								onToggleCheckChanged={handleToggleMetadata}
								text='Show Author Metadata'
							/>
						</Row>
					</SettingCard>
				</TemplateFormWrapper>
				<ImageFileChooserModal
					title='Upload Header Image'
					modalProps={{
						isOpen: imageModalOpen,
						onRequestClose: (result, cancel) => {
							if (!cancel && result) imageUpload.mutate({ files: [result], impersonationContext });
							setImageModalOpen(false);
						},
					}}
				/>
			</>
		);
	}
);

HomeDesignForm.displayName = 'HomeDesignForm';
